/* iter55g38 — Landing pricing pop-up.
 *
 * Fetches the live plan catalog from `/api/subscriptions/plans` and
 * renders all tiers side-by-side. Each card has a "Choose" CTA. For
 * signed-out visitors, "Choose" routes into the Writers Salon signup
 * modal (the Trial card also inherits the [recommended] visual
 * badge). For signed-in users, the card can hop into the checkout via
 * `subscriptionsApi.startCheckout` — but on the landing page the
 * primary use is signup, so we default to `onSelect(plan)`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Sparkles, Star, X, AlertTriangle, Calculator, ArrowRight, TrendingUp, DollarSign, Ban } from 'lucide-react';
import { usePaymentsStatus, logDowntimeClick } from '@/lib/payments_status';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// iter55g41 — Tiny slider helper for the cost estimator.
function SliderInput({ label, testid, min, max, step, value, onChange, unit }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
          {label}
        </label>
        <span className="text-[12px] font-mono-stat font-semibold text-neutral-800"
              data-testid={`${testid}-val`}>
          {value}<span className="ml-0.5 text-[10px] text-neutral-500">{unit}</span>
        </span>
      </div>
      <input
        type="range"
        data-testid={testid}
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        className="mt-1 w-full accent-oxblood-600"
      />
    </div>
  );
}

function PlanCard({ plan, onSelect, highlight, annualPromo, estimate, roi, adminMargins, editorAddonEnabled, onToggleEditorAddon, paymentsOff, founding, billingMode }) {
  const price = plan?.monthly?.price_usd ?? 0;
  // When BYOK mode is on and the plan supports it, show the BYOK price.
  const byokMonthly = plan?.byok_monthly_price_usd ?? null;
  const byokYearly  = plan?.byok_yearly_price_usd  ?? null;
  const isByokMode  = billingMode === 'byok' && plan?.byok && byokMonthly != null;
  const displayMonthly = isByokMode ? byokMonthly : price;
  const isFree = price === 0;
  // iter-payments-master — paid tiers are locked out when the admin
  // has flipped the master switch OFF. Free/trial tiers stay open so
  // signup + trial paths still work during a payment outage.
  const lockedOut = paymentsOff && !isFree;
  // iter-tier1 — Premier Studio "revenue engine" spotlight. Renders a
  // distinct amber banner on the Premier card so shoppers see it as the
  // book-factory plan, not just "the expensive one".
  const spotlight = plan?.spotlight;
  const isPremier = plan?.id === 'premier';
  // iter55z-audit — Premier editor add-on flag surfaces the $49/mo
  // add-on price so the frontend can render a toggle without a
  // second server round-trip.
  const editorAddonUsd = plan?.editor_pass_addon_price_usd || null;
  // iter-tier1 — ROI verdict: how many times over does the projected
  // author revenue cover the monthly plan cost.
  const roiVerdict = useMemo(() => {
    if (!roi || price <= 0) return null;
    const multiple = roi.netAuthor / price;
    if (multiple >= 10) return { tone: 'ok',   text: `Covers plan ${multiple.toFixed(0)}× over` };
    if (multiple >= 3)  return { tone: 'ok',   text: `Covers plan ${multiple.toFixed(1)}× over` };
    if (multiple >= 1)  return { tone: 'warn', text: `Just covers the plan (${multiple.toFixed(1)}×)` };
    return { tone: 'over', text: `Short by $${(price - roi.netAuthor).toFixed(0)} / mo` };
  }, [roi, price]);
  // iter55g41 — Estimator verdict per card.
  const verdict = useMemo(() => {
    if (!estimate) return null;
    const capCents = plan?.numeric_caps?.max_llm_spend_cents ?? null;
    const tokenBudget = plan?.monthly?.tokens ?? null;
    const overSpend  = capCents != null && estimate.usdSpend > capCents / 100;
    const overTokens = tokenBudget != null && estimate.tokens > tokenBudget;
    if (overSpend || overTokens) {
      return {
        state: 'over',
        text: overTokens
          ? `Exceeds ${(tokenBudget / 1e6).toFixed(0)}M token allowance`
          : `Exceeds $${(capCents / 100).toFixed(0)} spend ceiling`,
      };
    }
    if (capCents != null && estimate.usdSpend > (capCents / 100) * 0.8) {
      return { state: 'warn', text: `Uses ~${Math.round((estimate.usdSpend / (capCents / 100)) * 100)}% of your $${(capCents / 100).toFixed(0)} ceiling` };
    }
    return { state: 'ok', text: 'Fits comfortably' };
  }, [estimate, plan]);
  return (
    <div
      data-testid={`landing-plan-card-${plan.id}`}
      className={
        'flex flex-col rounded-xl border p-5 shadow-md transition '
        + (isPremier
          ? 'border-oxblood-700 bg-gradient-to-b from-amber-50 via-white to-white ring-2 ring-oxblood-500/60 shadow-lg'
          : highlight
          ? 'border-amber-500 bg-amber-50/70 ring-2 ring-amber-400/60'
          : plan.best
            ? 'border-oxblood-500 bg-white ring-1 ring-oxblood-400/40'
            : 'border-neutral-200 bg-white')
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[16px] font-semibold text-neutral-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {plan.name}
        </h3>
        {isPremier && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-oxblood-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
            data-testid="landing-plan-premier-badge"
          >
            <Sparkles className="h-3 w-3" /> Volume production
          </span>
        )}
        {highlight && !isPremier && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            <Star className="h-3 w-3" /> Recommended
          </span>
        )}
        {plan.best && !highlight && !isPremier && (
          <span className="inline-flex items-center gap-1 rounded-full bg-oxblood-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            <Sparkles className="h-3 w-3" /> Most popular
          </span>
        )}
        {founding?.active && (founding.eligible_tiers || []).includes(plan.id) && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
            data-testid={`landing-plan-founding-badge-${plan.id}`}
            title={`Founding Member: ${founding.percent_off}% off for ${founding.months} months, code ${founding.code} — ${founding.redemptions_remaining} spots left${founding.redeem_by ? `, ends ${new Date(founding.redeem_by).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}`}
          >
            <Sparkles className="h-3 w-3" /> −{founding.percent_off}% × {founding.months}mo
          </span>
        )}
      </div>
      <p className="mt-1 text-[12px] text-neutral-500">{plan.tagline}</p>
      {/* iter-tier1 — Premier Studio revenue spotlight */}
      {isPremier && spotlight && (
        <div
          className="mt-3 rounded-md border border-oxblood-300 bg-oxblood-50/60 p-3"
          data-testid="landing-plan-premier-spotlight"
        >
          <div className="text-[11px] font-bold uppercase tracking-wider text-oxblood-700">
            {spotlight.headline}
          </div>
          <div className="mt-0.5 text-[12px] text-neutral-800 leading-snug">
            {spotlight.subhead}
          </div>
          <ul className="mt-2 space-y-0.5 text-[11.5px] text-neutral-700">
            {spotlight.throughput && (
              <li className="flex items-start gap-1.5">
                <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-oxblood-600" />
                <span><strong>Throughput:</strong> {spotlight.throughput}</span>
              </li>
            )}
            {spotlight.revenue_hint && (
              <li className="flex items-start gap-1.5">
                <Calculator className="mt-0.5 h-3 w-3 shrink-0 text-oxblood-600" />
                <span className="italic">{spotlight.revenue_hint}</span>
              </li>
            )}
          </ul>
        </div>
      )}
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-[28px] font-bold font-mono-stat text-neutral-900">
          {isFree ? 'Free' : `$${displayMonthly.toFixed(0)}`}
        </span>
        {!isFree && <span className="text-[12px] text-neutral-500">/ month</span>}
        {!isFree && isByokMode && (
          <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">BYOK</span>
        )}
        {isFree && plan.trial_duration_days && (
          <span className="text-[12px] text-neutral-500">for {plan.trial_duration_days} days</span>
        )}
      </div>
      {/* Standard price shown as crossed-out when BYOK is active */}
      {isByokMode && (
        <p className="text-[11px] text-neutral-400" data-testid={`landing-plan-byok-savings-${plan.id}`}>
          <span className="line-through">${price.toFixed(2)}/mo standard</span>
          {' · '}
          <span className="font-semibold text-amber-700">
            ${Math.round((1 - byokMonthly / price) * 100)}% off
          </span>
        </p>
      )}
      {plan?.yearly?.price_usd > 0 && !isByokMode && (() => {
        // iter-p2-u — Dollar-anchor the yearly discount instead of "20%".
        // Users convert on concrete numbers ("Save $430/year") far
        // better than on percentages. Rounded to nearest dollar.
        const savings = Math.max(0,
          Math.round(plan.monthly.price_usd * 12 - plan.yearly.price_usd));
        // Feb-2026 · When admin flips `annual_plans_promo` on we
        // upgrade the badge from muted grey to a bold emerald pill so
        // it visually competes with the monthly price above.
        const promo = annualPromo;
        if (promo) {
          return (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10.5px] font-semibold text-white shadow-sm"
                 data-testid={`landing-plan-yearly-savings-${plan.id}`}>
              Save ${savings.toLocaleString()}/yr · ${plan.yearly.price_usd.toFixed(0)} annual
            </div>
          );
        }
        return (
          <p className="mt-0.5 text-[10.5px] text-neutral-500"
             data-testid={`landing-plan-yearly-savings-${plan.id}`}>
            or ${plan.yearly.price_usd.toFixed(0)}/yr —{' '}
            <b className="text-emerald-700">save ${savings.toLocaleString()}</b> vs. paying monthly
          </p>
        );
      })()}
      {/* iter55g41 — Estimator verdict pill. */}
      {/* BYOK yearly price — show when BYOK mode and plan has yearly option */}
      {isByokMode && byokYearly && (
        <p className="mt-0.5 text-[10.5px] text-neutral-500" data-testid={`landing-plan-byok-yearly-${plan.id}`}>
          or ${byokYearly.toFixed(0)}/yr ·{' '}
          <span className="font-semibold text-amber-700">
            save ${Math.round(byokMonthly * 12 - byokYearly)}/yr
          </span>
        </p>
      )}
      {/* iter55g41 — Estimator verdict pill. */}
      {verdict && (
        <div
          data-testid={`landing-plan-verdict-${plan.id}`}
          className={
            'mt-2 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold '
            + (verdict.state === 'ok'
              ? 'bg-emerald-100 text-emerald-900'
              : verdict.state === 'warn'
                ? 'bg-amber-100 text-amber-900'
                : 'bg-rose-100 text-rose-900')
          }
        >
          {verdict.state === 'ok' && <Check className="h-3 w-3" />}
          {verdict.state === 'warn' && <AlertTriangle className="h-3 w-3" />}
          {verdict.state === 'over' && <X className="h-3 w-3" />}
          {verdict.text}
        </div>
      )}
      {/* iter-tier1 — ROI verdict pill. Shown when the calculator is open. */}
      {roiVerdict && (
        <div
          data-testid={`landing-plan-roi-${plan.id}`}
          className={
            'mt-1 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold '
            + (roiVerdict.tone === 'ok'
              ? 'bg-emerald-100 text-emerald-900'
              : roiVerdict.tone === 'warn'
                ? 'bg-amber-100 text-amber-900'
                : 'bg-rose-100 text-rose-900')
          }
        >
          <TrendingUp className="h-3 w-3" />
          {roiVerdict.text}
        </div>
      )}
      <ul className="mt-4 space-y-1.5">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-[12.5px] text-neutral-700">
            <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {/* iter55z-audit — Premier Editor Pass toggle ($49/mo add-on).
          Base Premier no longer includes writer_coaching or the monthly
          editor review; this toggle re-adds them and appends the add-on
          to the Stripe checkout as a second line item.
          iter55z-audit round 9 (extras 3) — Editor Trust gold seal
          anchors the add-on visually. Nano Banana-generated. */}
      {isPremier && editorAddonUsd && (
        <label
          className="relative mt-4 flex cursor-pointer items-start gap-2 rounded-md border border-oxblood-300 bg-oxblood-50/40 p-3 pr-16 text-[12px] text-neutral-800"
          data-testid="landing-plan-premier-editor-addon-toggle"
        >
          <img
            src="/seals/editor-trust.png"
            alt="Editor Trust"
            data-testid="landing-seal-editor-trust"
            className="pointer-events-none absolute right-2 top-1/2 h-12 w-12 -translate-y-1/2 select-none drop-shadow"
            style={{ transform: `translateY(-50%) rotate(${(Math.random() * 6 - 3).toFixed(2)}deg)` }}
            draggable={false}
          />
          <input
            type="checkbox"
            className="mt-0.5 accent-oxblood-700"
            checked={!!editorAddonEnabled}
            onChange={(e) => onToggleEditorAddon?.(e.target.checked)}
            data-testid="landing-plan-premier-editor-addon-checkbox"
          />
          <div>
            <div className="font-semibold text-oxblood-800">
              + ${editorAddonUsd.toFixed(0)}/mo — Editor Pass
            </div>
            <div className="text-[11px] text-neutral-600">
              Monthly human proofreader pass + 30-min writer coaching call.
              Turns on <code>writer_coaching</code> and{' '}
              <code>monthly_editor_review</code> on your account.
            </div>
          </div>
        </label>
      )}
      {/* iter55z-audit — Admin-only live margin overlay on the pricing
          card. Only rendered when the current session belongs to an
          admin (via the /admin/financials/cogs-analyzer probe below). */}
      {adminMargins && (
        <div
          className="mt-3 rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-2 text-[10.5px] text-neutral-600"
          data-testid={`landing-plan-admin-margins-${plan.id}`}
        >
          <div className="mb-0.5 font-semibold uppercase tracking-wider text-neutral-500">
            Admin · live gross margin
          </div>
          <div className="flex flex-wrap gap-3 font-mono">
            <span>@ cap: <b className={adminMargins.at_cap < 20 ? 'text-rose-700' : adminMargins.at_cap < 35 ? 'text-amber-700' : 'text-emerald-700'}>{adminMargins.at_cap.toFixed(1)}%</b></span>
            <span>@ 50%: <b className="text-neutral-800">{adminMargins.at_50pct.toFixed(1)}%</b></span>
            <span>@ 30%: <b className="text-neutral-800">{adminMargins.at_30pct.toFixed(1)}%</b></span>
          </div>
        </div>
      )}
      <div className="mt-auto pt-5">
        <Button
          data-testid={`landing-plan-select-${plan.id}`}
          data-pay-button={lockedOut ? '1' : undefined}
          onClick={() => {
            if (lockedOut) {
              logDowntimeClick('landing-pricing', `landing-plan-select-${plan.id}`, { tierOrPack: plan.id });
            }
            onSelect?.(plan);
          }}
          disabled={lockedOut}
          variant={isPremier || highlight || plan.best ? 'default' : 'outline'}
          className={
            'w-full '
            + (isPremier ? 'bg-oxblood-700 hover:bg-oxblood-800 text-white shadow-md' : '')
          }
        >
          {lockedOut ? (
            <span className="inline-flex items-center gap-1.5"><Ban className="h-3.5 w-3.5" /> Payments paused</span>
          ) : isFree
            ? 'Start free trial'
            : isPremier
              ? (spotlight?.cta || 'Start filling your bookshelf')
              : `Choose ${plan.name}`}
        </Button>
      </div>
    </div>
  );
}

export default function LandingPricingDialog({ open, onOpenChange, onSelect }) {
  const payments = usePaymentsStatus();               // iter-payments-master
  const paymentsOff = payments.ready && !payments.enabled;
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  // iter55z-audit — Premier editor add-on toggle state (single boolean;
  // Premier is the only tier that reads it).
  const [editorAddon, setEditorAddon] = useState(false);
  // iter55z-audit — Admin-only live margin overlay. When the current
  // session is an admin, we fetch /admin/financials/cogs-analyzer and
  // build a map of tier_id → {at_cap, at_50pct, at_30pct}. On non-admin
  // sessions the fetch 401/403s silently and we render nothing.
  const [adminMargins, setAdminMargins] = useState({});
  // iter55g41 — Usage-based cost estimator inputs. Defaults are
  // "moderate novelist" — 8 chapters/month, 4 revision passes/month,
  // 30 chat msgs/month.
  const [chapters, setChapters]     = useState(8);
  const [revisions, setRevisions]   = useState(4);
  const [chats,    setChats]        = useState(30);
  const [showEstimator, setShowEstimator] = useState(false);
  // iter-tier1 — Studio ROI Calculator. Extends the cost estimator with
  // a revenue projection so shoppers weigh Premier not just against
  // token spend but against the earnings the plan enables.
  const [showRoi, setShowRoi]           = useState(false);
  const [booksPerMonth, setBooksPerMonth] = useState(3);   // Premier throughput floor
  const [avgPriceUsd,   setAvgPriceUsd]   = useState(4.99);
  const [salesPerBook,  setSalesPerBook]  = useState(50);
  // Feb-2026 · Revenue-lifts feature flags — enables the emerald annual
  // promo pill on each PlanCard when admin has flipped the flag on.
  const [enabledFlags, setEnabledFlags] = useState(new Set());
  const [founding, setFounding] = useState(null);
  // BYOK billing mode toggle — "standard" shows platform prices,
  // "byok" shows the discounted bring-your-own-key prices.
  const [billingMode, setBillingMode] = useState('standard');
  const AUTHOR_SHARE = 0.85;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr('');
    fetch(`${API}/subscriptions/plans`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setPlans(d.plans || []))
      .catch(() => setErr('Could not load plans. Please try again.'))
      .finally(() => setLoading(false));
    // 2026-10 — Founding Member launch coupon (public, no auth needed).
    fetch(`${API}/subscriptions/founding-promo`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setFounding)
      .catch(() => {});
    // iter55z-audit — admin-only margin overlay. Non-admin sessions
    // get 401/403 here and we swallow it silently.
    fetch(`${API}/admin/financials/cogs-analyzer`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.tiers) return;
        const map = {};
        for (const t of d.tiers) {
          map[t.tier_id] = {
            at_cap:    t.scenarios?.at_cap?.margin_pct    ?? t.margin_pct,
            at_50pct:  t.scenarios?.at_50pct?.margin_pct  ?? 0,
            at_30pct:  t.scenarios?.at_30pct?.margin_pct  ?? 0,
          };
        }
        setAdminMargins(map);
      })
      .catch(() => { /* not an admin — silent */ });
    // Feb-2026 · Revenue-lifts: pull the list of enabled flags so the
    // dialog can highlight annual pricing (or any future promo lever)
    // without a page reload after an admin flip.
    fetch(`${API}/revenue-features/mine`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.enabled) return;
        setEnabledFlags(new Set(d.enabled.map((f) => f.key)));
      })
      .catch(() => { /* signed-out visitor — silent */ });
  }, [open]);

  // iter55g41 — Token-cost heuristics based on real endpoint usage
  //   • Chapter draft   ≈ 15 000 tokens (context + output)
  //   • Revision read   ≈ 30 000 tokens (full re-read + suggestions)
  //   • Chat message    ≈    400 tokens (turn + short reply)
  // At Claude Sonnet blended pricing (~$8/M input, $15/M output;
  // roughly $10/M averaged across our mix), $/tokens = tokens * 1e-5.
  const estimate = useMemo(() => {
    const tokens = chapters * 15000 + revisions * 30000 + chats * 400;
    const usdSpend = tokens / 1_000_000 * 10;  // $10 per 1 M tokens
    return { tokens, usdSpend };
  }, [chapters, revisions, chats]);

  // iter-tier1 — ROI projection. Multiplied by author share (85% of gross)
  // and rendered per-plan as a "covers plan X times" verdict pill.
  const roi = useMemo(() => {
    const gross = booksPerMonth * avgPriceUsd * salesPerBook;
    const netAuthor = gross * AUTHOR_SHARE;
    return {
      gross:     gross,
      netAuthor: netAuthor,
      annual:    netAuthor * 12,
    };
  }, [booksPerMonth, avgPriceUsd, salesPerBook]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="landing-pricing-dialog"
        className="max-w-6xl flex flex-col bg-neutral-50 p-0 sm:p-0 overflow-hidden"
      >
        <DialogHeader className="border-b border-neutral-200 bg-white px-6 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-[18px] font-semibold text-neutral-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Choose your writing plan
              </DialogTitle>
              <p className="text-[12.5px] text-neutral-500">
                Every plan includes the Story Bible, Character Bible, and manuscript editor. AI features scale up as your novel does.
              </p>
            </div>
            {/* BYOK Standard / BYOK toggle */}
            <div className="flex shrink-0 items-center gap-1 rounded-full border border-neutral-200 bg-neutral-100 p-0.5" data-testid="billing-mode-toggle">
              <button
                type="button"
                data-testid="billing-mode-standard"
                onClick={() => setBillingMode('standard')}
                className={`rounded-full px-3 py-1 text-[11.5px] font-semibold transition-all ${
                  billingMode === 'standard'
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                Standard
              </button>
              <button
                type="button"
                data-testid="billing-mode-byok"
                onClick={() => setBillingMode('byok')}
                className={`rounded-full px-3 py-1 text-[11.5px] font-semibold transition-all ${
                  billingMode === 'byok'
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                BYOK — Save up to 58%
              </button>
            </div>
          </div>
          {billingMode === 'byok' && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800" data-testid="byok-mode-notice">
              <strong>Bring Your Own Key:</strong> Paste your OpenAI, Anthropic, or Gemini API key in Settings → AI Keys. Every draft bills your provider directly — we charge only the platform fee shown below.
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5"
             style={{ minHeight: 0 }}>
          {loading && (
            <div className="py-16 text-center text-[13px] text-neutral-500" data-testid="landing-pricing-loading">
              Loading plans…
            </div>
          )}
          {err && (
            <div className="py-8 text-center text-[13px] text-red-600" data-testid="landing-pricing-error">
              {err}
            </div>
          )}
          {!loading && !err && (
            <>
              {/* iter55g41 — Usage-based cost estimator. Collapsed by
                  default so first-time visitors see plan cards
                  immediately; power users click to project their
                  actual usage against each tier's ceiling. */}
              <div className="mb-4 rounded-md border border-neutral-200 bg-white">
                <button
                  type="button"
                  data-testid="landing-estimator-toggle"
                  onClick={() => setShowEstimator((v) => !v)}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-semibold text-neutral-800 hover:bg-neutral-50"
                >
                  <Calculator className="h-4 w-4 text-oxblood-600" />
                  Not sure which fits? Estimate your monthly cost
                  <span className="ml-auto text-[11px] font-normal text-neutral-500">
                    {showEstimator ? 'Hide' : 'Show estimator'}
                  </span>
                </button>
                {showEstimator && (
                  <div className="grid grid-cols-1 gap-4 border-t border-neutral-200 p-4 md:grid-cols-4"
                       data-testid="landing-estimator-body">
                    <SliderInput
                      label="Chapters drafted"      testid="est-chapters"
                      min={0} max={100} step={1}
                      value={chapters}  onChange={setChapters}
                      unit="/mo"
                    />
                    <SliderInput
                      label="Revision read-throughs" testid="est-revisions"
                      min={0} max={30} step={1}
                      value={revisions} onChange={setRevisions}
                      unit="/mo"
                    />
                    <SliderInput
                      label="Chat messages"          testid="est-chats"
                      min={0} max={500} step={5}
                      value={chats}     onChange={setChats}
                      unit="/mo"
                    />
                    <div className="flex flex-col justify-center rounded-md bg-neutral-50 p-3 text-center"
                         data-testid="landing-estimator-total">
                      <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">Projected AI cost</div>
                      <div className="mt-0.5 text-[22px] font-bold font-mono-stat text-oxblood-700">
                        ${estimate.usdSpend.toFixed(2)}
                      </div>
                      <div className="text-[10.5px] text-neutral-500">
                        ~{(estimate.tokens / 1e6).toFixed(2)} M tokens
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* iter-tier1 — Studio ROI Calculator. Optional collapsible
                  panel that projects author revenue at N books × price ×
                  sales / month, and drives the per-card ROI pill. */}
              <div className="mb-4 rounded-md border border-neutral-200 bg-white">
                <button
                  type="button"
                  data-testid="landing-roi-toggle"
                  onClick={() => setShowRoi((v) => !v)}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-semibold text-neutral-800 hover:bg-neutral-50"
                >
                  <TrendingUp className="h-4 w-4 text-oxblood-600" />
                  Weigh plans by projected earnings, not just tokens
                  <span className="ml-auto text-[11px] font-normal text-neutral-500">
                    {showRoi ? 'Hide' : 'Show ROI calculator'}
                  </span>
                </button>
                {showRoi && (
                  <div className="grid grid-cols-1 gap-4 border-t border-neutral-200 p-4 md:grid-cols-4"
                       data-testid="landing-roi-body">
                    <SliderInput
                      label="Books published"        testid="roi-books"
                      min={1} max={10} step={1}
                      value={booksPerMonth} onChange={setBooksPerMonth}
                      unit="/mo"
                    />
                    <div>
                      <div className="flex items-baseline justify-between">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                          Avg book price
                        </label>
                        <span className="text-[12px] font-mono-stat font-semibold text-neutral-800"
                              data-testid="roi-price-val">
                          ${avgPriceUsd.toFixed(2)}
                        </span>
                      </div>
                      <input
                        type="range" data-testid="roi-price"
                        min={0.99} max={24.99} step={0.5}
                        value={avgPriceUsd}
                        onChange={(e) => setAvgPriceUsd(parseFloat(e.target.value) || 4.99)}
                        className="mt-1 w-full accent-oxblood-600"
                      />
                    </div>
                    <SliderInput
                      label="Sales per book"          testid="roi-sales"
                      min={0} max={500} step={5}
                      value={salesPerBook} onChange={setSalesPerBook}
                      unit=" / mo"
                    />
                    <div className="flex flex-col justify-center rounded-md bg-oxblood-50 p-3 text-center"
                         data-testid="landing-roi-total">
                      <div className="text-[10.5px] uppercase tracking-wider text-oxblood-700">
                        Projected author revenue
                      </div>
                      <div className="mt-0.5 text-[22px] font-bold font-mono-stat text-oxblood-800">
                        ${roi.netAuthor.toFixed(0)}
                        <span className="text-[11px] font-normal text-oxblood-600"> / mo</span>
                      </div>
                      <div className="text-[10.5px] text-oxblood-600">
                        ${(roi.annual).toFixed(0)} / year · 85% author share
                      </div>
                    </div>
                    <div className="md:col-span-4 rounded-sm bg-neutral-50 p-2 text-[10.5px] text-neutral-600">
                      <DollarSign className="mr-1 inline h-3 w-3" />
                      Numbers reflect the 85/15 revenue share on your Bookstore sales.
                      Real earnings depend on marketing, cover, and price sensitivity —
                      the ROI pill on each card compares this projection to the plan cost.
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {plans.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    highlight={!!p.recommended}
                    annualPromo={enabledFlags.has('annual_plans_promo')}
                    founding={founding}
                    billingMode={billingMode}
                    onSelect={(plan) => onSelect?.(plan, {
                      with_editor_addon: plan.id === 'premier' && editorAddon,
                      billing_mode: billingMode,
                    })}
                    estimate={showEstimator ? estimate : null}
                    roi={showRoi ? roi : null}
                    adminMargins={adminMargins[p.id]}
                    editorAddonEnabled={p.id === 'premier' && editorAddon}
                    onToggleEditorAddon={p.id === 'premier' ? setEditorAddon : undefined}
                    paymentsOff={paymentsOff}
                  />
                ))}
              </div>
            </>
          )}
          <div className="mt-6 rounded-md border border-neutral-200 bg-white p-3 text-center text-[11.5px] text-neutral-500">
            All plans include a <strong>$ spend ceiling</strong> that pauses AI when you hit it — no surprise bills.
            Upgrade or downgrade any time. Yearly billing saves 20%.
          </div>
          {/* iter55g42 — Human-help escape hatch for visitors who see
              the estimator, feel unsure, and would otherwise bounce. */}
          <div className="mt-3 flex flex-col items-center gap-1 text-center">
            <span className="text-[11px] text-neutral-500">Still not sure which plan is right for your novel?</span>
            <a
              href="mailto:info@crownnrose.com?subject=I%20have%20questions%20about%20which%20plan&body=Hi%20Crown%20%26%20Rose%20team%2C%0A%0AI%27m%20considering%20one%20of%20your%20writing%20plans%20and%20would%20love%20a%20quick%20human%20perspective.%20A%20bit%20about%20my%20project%3A%0A%0A%E2%80%A2%20Genre%3A%0A%E2%80%A2%20Target%20length%3A%0A%E2%80%A2%20How%20often%20I%27ll%20be%20writing%3A%0A%E2%80%A2%20What%20I%27m%20unsure%20about%3A%0A%0AThanks!"
              data-testid="landing-pricing-speak-human"
              className="inline-flex items-center gap-1.5 rounded-md border border-oxblood-300 bg-oxblood-50 px-3 py-1.5 text-[12px] font-semibold text-oxblood-700 transition hover:bg-oxblood-100"
            >
              Speak to a human
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
